import { useIntervalFn, useTimeoutFn, whenever } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref, shallowRef, watch } from 'vue';

export type GladlyChatStatus = 'AVAILABLE' | 'UNAVAILABLE_BUSY' | 'UNAVAILABLE_OFFICE_CLOSED';
export type StatusChangeCallback = (availability: GladlyChatStatus) => void;
type StatusChangeEventRegistration = (
  event: 'availability:change',
  callback: StatusChangeCallback,
) => void;

export interface GladlyInitializer {
  init: () => void;
  loaderCdn?: string;
}

export interface Gladly extends Omit<GladlyInitializer, 'loaderCdn'> {
  getAvailability: () => GladlyChatStatus;
  /**
   * Other events exist, but we only listen for availability changes.
   */
  on: StatusChangeEventRegistration;
  show: () => void;
}

export const WIDGET_ID = 'gladlyChat_container';

function getWidgetElement() {
  return document.getElementById(WIDGET_ID);
}

export function isGladly(value?: Gladly | GladlyInitializer): value is Gladly {
  return typeof value === 'object' && 'getAvailability' in value;
}

export const useChat = defineStore('chat', () => {
  const service = shallowRef<Gladly>();
  const status = ref<GladlyChatStatus>();
  const available = computed(() => status.value === 'AVAILABLE');
  const loaded = computed(() => !!service.value);
  whenever(service, (loadedService) => {
    loadedService.on('availability:change', (availability) => {
      status.value = availability;
    });
  });

  const suppressors = ref(0);
  const suppressed = computed(() => suppressors.value > 0);
  watch(suppressed, (currentlySuppressed) => {
    if (currentlySuppressed) {
      hideChatWidget();
    } else {
      showChatWidget();
    }
  });

  const poll = useIntervalFn(checkAvailability, 250, { immediate: false, immediateCallback: true });
  const timeout = useTimeoutFn(
    () => {
      poll.pause();
    },
    3000,
    { immediate: false },
  );
  whenever(status, () => {
    poll.pause();
    timeout.stop();
  });

  function checkAvailability() {
    if (isGladly(window.Gladly)) {
      service.value = window.Gladly;
      status.value = window.Gladly.getAvailability();
      return;
    }
    service.value = undefined;
    status.value = undefined;
  }

  function hideChatWidget() {
    const chatWidget = getWidgetElement();
    if (!chatWidget) return;
    chatWidget.style.visibility = 'hidden';
  }

  const pending = computed(() => poll.isActive.value);
  function init() {
    if (!pending.value && !loaded.value) {
      poll.resume();
      timeout.start();
    }
  }

  function open() {
    showChatWidget();
    service.value?.show();
  }

  function registerSuppression() {
    suppressors.value += 1;
  }

  function showChatWidget() {
    const chatWidget = getWidgetElement();
    if (!chatWidget || suppressed.value) return;
    chatWidget.style.visibility = 'visible';
  }

  function unregisterSuppression() {
    suppressors.value = Math.max(0, suppressors.value - 1);
  }

  return {
    available,
    checkAvailability,
    hideChatWidget,
    init,
    loaded,
    open,
    pending,
    poll,
    registerSuppression,
    service,
    showChatWidget,
    status,
    suppressed,
    suppressors,
    timeout,
    unregisterSuppression,
  };
});
